import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { useUsers } from "../../Query/useUsers";
import colors from "../../Constants/colors";
import { Features } from "../../Enums/companyStatus";
import { hasPermission } from "../../Helpers/authHelpers";
import { useTableParams } from "../../Core/useTableParams";
import { UserSortColumn } from "../../Constants/userTypes";
import { useDebouncedCallback } from "../../Hooks/useDebouncedCallback";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { ComoInput } from "../Common/ComoInput";

const Users = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("userPage");
  const { colors: workspace } = useWorkspaceColors();

  const {
    page,
    pageSize,
    search,
    sortColumn,
    sortOrder,
    setPage,
    setSearch,
    setSort,
  } = useTableParams<UserSortColumn>({
    defaultSortColumn: "name",
    defaultSortOrder: "asc",
    defaultPage: 1,
    defaultSearch: "",
    defaultPageSize: 10,
  });

  const [localSearch, setLocalSearch] = useState(search);

  const debouncedSetSearch = useDebouncedCallback((value: string) => {
    setSearch(value);
  }, 400);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalSearch(value);
    debouncedSetSearch(value);
  };

  const { data: users, isLoading } = useUsers(
    page,
    pageSize,
    search,
    sortColumn ?? undefined,
    sortOrder,
  );

  const hideLimits =
    hasPermission(Features.ORDER_AN_EXPERT) ||
    hasPermission(Features.DUSMANN_IMPORT);

  const sortableColumns: Array<{ key: UserSortColumn; label: string }> = [
    { key: "name", label: t("name") },
    { key: "surname", label: t("surname") },
    { key: "email", label: t("email") },
    { key: "workspace", label: t("workspace") },
    { key: "entity_limit", label: t("entityLimit") },
    { key: "evaluation_limit", label: t("requestsLimit") },
  ];

  const headerCellSx = {
    fontWeight: 600,
    color: colors.gray700,
    backgroundColor: colors.gray50,
    borderBottom: `1px solid ${colors.gray200}`,
    fontSize: "0.8rem",
    whiteSpace: "nowrap" as const,
  };

  const bodyCellSx = {
    color: colors.primary_dark,
    fontSize: "0.85rem",
    borderBottom: `1px solid ${colors.gray100}`,
  };

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        backgroundColor: colors.gray50,
      }}
    >
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          px: { xs: 2, sm: 3 },
          py: 4,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: colors.white,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <PeopleAltOutlinedIcon
                sx={{ fontSize: 32, color: workspace.primary_dark }}
              />
              <Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: workspace.primary_dark }}
                >
                  {t("title")}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.gray500 }}>
                  {users?.total ?? 0}
                </Typography>
              </Box>
            </Stack>

            <Tooltip title={t("createTooltip")}>
              <IconButton
                onClick={() => navigate("create")}
                sx={{
                  bgcolor: workspace.accent,
                  color: workspace.primary_dark,
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 1,
                  gap: 0.75,
                  "&:hover": { bgcolor: workspace.accent, opacity: 0.9 },
                }}
              >
                <AddCircleOutlineIcon sx={{ fontSize: 20 }} />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, fontSize: "0.85rem" }}
                >
                  {t("createTooltip")}
                </Typography>
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: colors.white,
          }}
        >
          <Box sx={{ mb: 2, maxWidth: { xs: "100%", sm: 360 } }}>
            <ComoInput
              placeholder={t("searchLabel")}
              value={localSearch}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: colors.gray400 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <TableContainer
            sx={{
              border: `1px solid ${colors.gray200}`,
              borderRadius: 2,
              overflow: "auto",
              position: "relative",
              minHeight: 200,
            }}
          >
            {isLoading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255,255,255,0.6)",
                  zIndex: 10,
                }}
              >
                <CircularProgress
                  size={32}
                  sx={{
                    color: workspace.accent,
                  }}
                />
              </Box>
            )}
            <Table size="small">
              <TableHead>
                <TableRow>
                  {sortableColumns.map((col) => {
                    if (
                      hideLimits &&
                      (col.key === "entity_limit" ||
                        col.key === "evaluation_limit")
                    ) {
                      return null;
                    }
                    return (
                      <TableCell key={col.key} sx={headerCellSx}>
                        <TableSortLabel
                          active={sortColumn === col.key}
                          direction={sortColumn === col.key ? sortOrder : "asc"}
                          onClick={() => setSort(col.key)}
                          sx={{
                            color: colors.gray700,
                            "&.Mui-active, &:hover": {
                              color: workspace.primary_dark,
                            },
                            "& .MuiTableSortLabel-icon": {
                              color: `${workspace.accent} !important`,
                            },
                          }}
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    );
                  })}
                  {!hideLimits && (
                    <TableCell sx={headerCellSx}>{t("models")}</TableCell>
                  )}
                  <TableCell sx={headerCellSx}>{t("status")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(users?.users?.length ?? 0) > 0
                  ? (users?.users ?? []).map((user) => (
                      <TableRow
                        key={user.id}
                        onClick={() => navigate(`/user/${user.id}`)}
                        sx={{
                          cursor: "pointer",
                          transition: "background-color 0.15s ease-in-out",
                          "&:hover": {
                            backgroundColor: `${workspace.primary_dark}08`,
                          },
                        }}
                      >
                        <TableCell sx={bodyCellSx}>{user.name}</TableCell>
                        <TableCell sx={bodyCellSx}>{user.surname}</TableCell>
                        <TableCell sx={bodyCellSx}>{user.email}</TableCell>
                        <TableCell sx={bodyCellSx}>
                          {user.workspace?.name ?? (
                            <Typography
                              variant="caption"
                              sx={{
                                color: colors.gray400,
                                fontStyle: "italic",
                              }}
                            >
                              {t("noWorkspace")}
                            </Typography>
                          )}
                        </TableCell>
                        {!hideLimits && (
                          <>
                            <TableCell sx={bodyCellSx}>
                              {user.companies_limit}
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              {user.evaluation_limit}
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              {user.accessible_model_ids?.length ?? 0}
                            </TableCell>
                          </>
                        )}
                        <TableCell sx={bodyCellSx}>
                          {user.disabled ? (
                            <Chip
                              label={t("disabled")}
                              size="small"
                              sx={{
                                bgcolor: colors.red50,
                                color: colors.red700,
                                fontWeight: 500,
                                border: `1px solid ${colors.red200}`,
                                fontSize: "0.7rem",
                                height: 22,
                              }}
                            />
                          ) : (
                            <Chip
                              label={t("active")}
                              size="small"
                              sx={{
                                bgcolor: colors.green50,
                                color: colors.green700,
                                fontWeight: 500,
                                border: `1px solid ${colors.green200}`,
                                fontSize: "0.7rem",
                                height: 22,
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  : !isLoading && (
                      <TableRow>
                        <TableCell
                          colSpan={hideLimits ? 5 : 8}
                          align="center"
                          sx={{
                            color: colors.gray500,
                            py: 6,
                            backgroundColor: colors.gray50,
                            fontSize: "0.875rem",
                          }}
                        >
                          {t("noUsers")}
                        </TableCell>
                      </TableRow>
                    )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 1,
            }}
          >
            <TablePagination
              rowsPerPageOptions={[]}
              component="div"
              count={users?.total ?? 0}
              rowsPerPage={pageSize}
              page={page - 1}
              onPageChange={(_, newPage) => setPage(newPage + 1)}
              labelRowsPerPage=""
              sx={{
                "& .MuiTablePagination-toolbar": {
                  minHeight: 44,
                  px: 0,
                },
                "& .MuiTablePagination-displayedRows": {
                  color: colors.gray600,
                  fontSize: "0.8rem",
                },
                "& .MuiIconButton-root": {
                  color: workspace.primary_dark,
                },
              }}
            />
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default Users;
